import type * as api from '@gitbook/api-client';
import { Block, type Change, type NodeType, type Value } from '@gitbook/slate';
import { assert, assertNever } from '@gitbook/util-ts';

import { createHeading1 } from '../heading';
import { createParagraph } from '../paragraph';
import {
    BLOCK_UPDATE,
    BLOCK_UPDATES,
    type BlockUpdatesData,
    type BlockUpdatesDateFormat,
} from '../types';

export function createUpdates(input: { format?: BlockUpdatesDateFormat } = {}): Block {
    const { format = 'full' } = input;

    return Block.create({
        type: BLOCK_UPDATES,
        data: { format } satisfies BlockUpdatesData,
        nodes: [createUpdate()],
    });
}

export function createUpdate(input: { date?: string | Date } = {}): Block {
    const { date = new Date() } = input;
    // Validate and (if needed) normalize the date=
    const dateYYYYMMDD = normalizeDate(date);

    return Block.create({
        type: BLOCK_UPDATE,
        data: { date: dateYYYYMMDD },
        nodes: [createHeading1(), createParagraph()],
    });
}

export function getCurrentUpdate(value: Value):
    | undefined
    | {
          updates: Block;
          update: Block;
      } {
    // Return early if selection is unset - guarantees a set key
    if (value.isSelectionUnset || !value.startKey) {
        return undefined;
    }
    const update = value.document.getClosest(
        value.startKey,
        (node: NodeType) => node.object === 'block' && node.type === BLOCK_UPDATE
    ) as Block | undefined;

    const updates = update ? value.document.getParent(update.key) : null;

    if (!update || updates?.object !== 'block') {
        return undefined;
    }

    return {
        updates,
        update,
    };
}

export function insertUpdateAt(change: Change, updatesBlockKey: string, index: number) {
    const newUpdate = createUpdate();
    change.insertNodeByKey(updatesBlockKey, index, newUpdate);
    change.collapseToStartOf(newUpdate);
    change.focus();
}

export function moveUpdate(change: Change, updateBlockKey: string, direction: 'up' | 'down') {
    const update = change.value.document.assertBlock(updateBlockKey);
    const updates = change.value.document.getParent(updateBlockKey);
    assert(update, 'Update block must exist');
    assert(updates, 'Update block must have a parent');

    switch (direction) {
        case 'up': {
            const previous = updates.getPreviousSibling(updateBlockKey);
            if (!previous) {
                return;
            }
            change.moveBeforeNodeByKey(update.key, previous.key);
            break;
        }
        case 'down': {
            const next = updates.getNextSibling(updateBlockKey);
            if (!next) {
                return;
            }
            change.moveAfterNodeByKey(update.key, next.key);
            break;
        }
        default:
            assertNever(direction);
    }
    change.focus();
}

/**
 * Validate if a date is in YYYY-MM-DD format and is valid.
 *
 * @param value - The date string to validate
 * @returns true if the date is in YYYY-MM-DD format and valid
 */
export function validateDateAsYYYYMMDD(value: string): boolean {
    // If a string in YYYY-MM-DD format is provided and valid
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
            return true;
        }
    }
    return false;
}

/**
 * Parse and validate a date attribute. Takes in these formats:
 * - YYYY-MM-DD
 * - ISO date strings (e.g., "2025-11-05T00:00:00.000Z")
 * - Date object.
 *
 * If invalid, returns the current date in YYYY-MM-DD format.
 *
 * @param value - The date value to parse (string | Date | undefined)
 * @returns A normalized date string in YYYY-MM-DD format
 */
export function normalizeDate(value?: string | Date): string {
    // If a string in YYYY-MM-DD format is provided and valid
    if (typeof value === 'string' && validateDateAsYYYYMMDD(value)) {
        return value;
    }

    // If a valid Date object is provided, format it to YYYY-MM-DD
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().split('T')[0];
    }

    // If a string that looks like an ISO date format, parse it and convert to YYYY-MM-DD
    if (typeof value === 'string') {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
            // Valid date string - convert to YYYY-MM-DD format
            return parsed.toISOString().split('T')[0];
        }
    }

    // Fallback: return current date in YYYY-MM-DD format
    return new Date().toISOString().split('T')[0];
}

/**
 * Check if a value has the shape of a ContentRefTag (possibly missing kind).
 * Returns the slug if valid, undefined otherwise.
 */
function getContentRefTagSlug(tag: unknown): string | undefined {
    if (typeof tag !== 'object' || tag === null) {
        return undefined;
    }

    const obj = tag as Record<string, unknown>;
    if (obj.kind !== undefined && obj.kind !== 'tag') {
        return undefined;
    }

    if (typeof obj.tag !== 'string' || obj.tag.length === 0) {
        return undefined;
    }

    return obj.tag;
}

/**
 * Normalize tags: returns the same reference if already valid, a normalized array if fixable,
 * or undefined if invalid/empty.
 *
 * Handles: adding missing `kind: 'tag'`, filtering invalid entries, deduplicating by slug.
 */
export function normalizeTags(tags: unknown): api.ContentRefTag[] | undefined {
    if (!Array.isArray(tags) || tags.length === 0) return undefined;

    const seen = new Set<string>();
    const result: api.ContentRefTag[] = [];
    let changed = false;

    for (const tag of tags) {
        const slug = getContentRefTagSlug(tag);
        if (!slug) {
            changed = true;
            continue;
        }
        if (seen.has(slug)) {
            changed = true;
            continue;
        }
        seen.add(slug);

        if ((tag as Record<string, unknown>).kind !== 'tag') changed = true;
        result.push({ kind: 'tag', tag: slug });
    }

    if (result.length === 0) {
        return undefined;
    }
    return changed ? result : (tags as api.ContentRefTag[]);
}
